import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import { Table, TableHead, TableBody, TableRow, TableCell, TablePagination, Stack, Box, Typography, Chip } from "@mui/material";
import LayerdIcon from "../../../../common/LayerdIcon";
import { DETECTION_ICONS } from "../../../../../icons/detectionLucideIcons";
import { ReactComponent as BackIcon } from "../../../../../vendor/_new/icon/detections/back.svg";

import { DetectionFilterCategory } from "../../../../../utils/constants";
import { formatDate } from "../../../../../utils/format";
import colors from "../../../../../utils/colors";
import { displayUserAgent } from "../../../../../utils/display";
import { SkeletonContent } from "../bot/DetectionDetail";
import useSite from "../../../../../hooks/user/useSite";
import EventDetailDrawer from "../../detection/EventDetailDrawer";

const tableHeadSx =  { fontSize: 14, lineHeight: "21px", fontWeight: "medium", color: colors.neutral[1000] };
const tableBodySx =  { fontSize: 14, lineHeight: "21px", color: colors.neutral[800] };
const regionNames = new Intl.DisplayNames(["en"], { type: "region" });

const safeRegionName = (countryCode) => {
  const code = (countryCode || "").toString().trim().toUpperCase();
  if (!code) return "—";
  try {
    return regionNames.of(code) || code;
  } catch {
    return code;
  }
};

const displayTruncated = (value, maxLen) => {
  const str = (value ?? "").toString().trim();
  if (!str) return "—";
  return str.length > maxLen ? str.substr(0, maxLen) + " ..." : str;
};

export const getCategoryTitle = (categoryId) => {
  switch (categoryId) {
    case DetectionFilterCategory.TopWAFDetectionsByCountry:
      return "Top WAF detections by country";
    case DetectionFilterCategory.TopUserAgent:
      return "Top user agent";
    case DetectionFilterCategory.TopPath:
      return "Top paths";
    case DetectionFilterCategory.TopEventBySource:
      return "Top events by source";
    case DetectionFilterCategory.TopMethod:
      return "Top methods";
    case DetectionFilterCategory.TopDetection:
      return "Top detections";
    default:
      return "Top WAF detections by country";
  }
};

export const getEventTitle = (categoryId, value) => {
  if (categoryId === DetectionFilterCategory.TopWAFDetectionsByCountry) {
    return safeRegionName(value || "US");
  } else if (categoryId === DetectionFilterCategory.TopUserAgent || categoryId === DetectionFilterCategory.TopPath) {
    return displayTruncated(value, 48);
  } else {
    return (value ?? "").toString().trim() || "—";
  }
};

const WebApplicationwafDetectionDetail = () => {
  const navigate = useNavigate();
  const { categoryId } = useParams();
  const [searchParams] = useSearchParams();

  const { events, total_events_count, from, rows_per_page, getOnlyEvents } = useSite();

  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [page, setPage] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);

  let parsedParams = {};
  try {
    const raw = searchParams.get("data");
    parsedParams = raw ? JSON.parse(decodeURIComponent(raw)) : {};
  } catch (e) {
    // Fallback to safe defaults if the query param is malformed
    parsedParams = {};
  }

  const siteID = parsedParams?.siteID || "all";
  const timeRange = parsedParams?.timeRange || "7d";
  const data = parsedParams?.data;

  useEffect(() => {
    setPage(from / rows_per_page);
  }, [from]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    setRowsPerPage(rows_per_page);
  }, [rows_per_page]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    getOnlyEvents(siteID, timeRange, 5, 0, [{ key: categoryId, value: data, condition: "eq" }]);
    // eslint-disable-next-line
  }, [categoryId, getOnlyEvents]);

  const handleChangePage = async (event, newPage) => {
    setPage(newPage);
    getOnlyEvents(siteID, timeRange, rowsPerPage, rowsPerPage * newPage, [{ key: categoryId, value: data, condition: "eq" }]);
  };

  const handleChangeRowsPerPage = async (event) => {
    setPage(0);
    setRowsPerPage(parseInt(event.target.value, 10));
    getOnlyEvents(siteID, timeRange, parseInt(event.target.value, 10), 0, [{ key: categoryId, value: data, condition: "eq" }]);
  };

  const handleEventClick = (eventItem) => {
    setSelectedEvent(eventItem);
    setDrawerOpen(true);
  };

  const handleDrawerClose = () => {
    setDrawerOpen(false);
    setSelectedEvent(null);
  };

  return (
    <>
      <Helmet title="WAF Dashboard" />
      <Stack spacing={6} sx={{ p: 6 }}>
        <Stack
          direction={"row"}
          alignItems={"center"}
          spacing={1}
          onClick={() =>
            navigate(
              `/v2/detection/web_application/dashboard/${categoryId}?data=${encodeURIComponent(JSON.stringify({ siteID, timeRange }))}`
            )
          }
          sx={{ cursor: "pointer" }}
        >
          <BackIcon style={{ color: colors.primary[300] }} />
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            WAF Dashboard
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            /
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.primary[300] }}>
            {getCategoryTitle(categoryId)}
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            /
          </Typography>
          <Typography sx={ { fontSize: 16, lineHeight: "24px", fontWeight: "medium", color: colors.neutral[800] }}>
            {getEventTitle(categoryId, data)}
          </Typography>
        </Stack>
        <Stack>
          <Box spacing={6} sx={{ border: `solid 1px ${colors.neutral[200]}`, borderRadius: "8px", p: 2 }}>
            <Table
              sx={{
                "& .MuiTableCell-head": { py: 3, px: 3 },
                "& .MuiTableCell-body": { py: 2, px: 3 },
                "& .MuiTableCell-root": { borderBottom: "none" },
              }}
            >
              <TableHead>
                <TableRow>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Timestamp</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Source IP</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Hostname</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>URI</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>User agent</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Type of detection</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>Response status</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}>HTTP method</Typography>
                  </TableCell>
                  <TableCell>
                    <Typography sx={tableHeadSx}></Typography>
                  </TableCell>
                </TableRow>
              </TableHead>
              {events === null && <SkeletonContent rowsPerPage={rowsPerPage} />}
              <TableBody>
                {events?.map((item, index) => (
                  <TableRow key={`detail_data_${index}`} onClick={() => handleEventClick(item)} sx={{ cursor: "pointer" }}>
                    <TableCell>
                      <Typography sx={tableBodySx}>{formatDate(item?.timestamp) || "—"}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.src_ip}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.host_name}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {item?.uri && item?.uri.length > 24 ? item?.uri.substr(0, 24) + " ..." : item?.uri}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>
                        {displayTruncated(displayUserAgent(item?.ua), 24)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Stack spacing={1} alignItems={"flex-start"}>
                        {item?.type?.map((type, typeIndex) => {
                          const label =
                            type == null
                              ? "—"
                              : typeof type === "object"
                              ? type?.msg ?? type?.ruleId ?? (type?.severity != null ? String(type.severity) : "—")
                              : String(type);
                          return (
                            <Chip
                              key={type?.ruleId ?? type?.msg ?? typeIndex}
                              size="small"
                              label={label}
                              sx={{ px: 1, background: colors.yellow[100], borderRadius: "30px", ...tableBodySx }}
                            />
                          );
                        })}
                      </Stack>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.resStatus}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={tableBodySx}>{item?.method}</Typography>
                    </TableCell>
                    <TableCell>
                      <Stack
                        alignItems={"flex-end"}
                      >
                        <LayerdIcon icon={DETECTION_ICONS.view} size={18} color="neutral" />
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {events?.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={9}>
                      <Typography sx={tableBodySx} textAlign="center">
                        There are no events
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Box>
          <TablePagination
            rowsPerPageOptions={[5, 10]}
            component="div"
            count={total_events_count}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            sx={{
              "& .MuiTablePagination-selectLabel": { },
              "& .MuiTablePagination-displayedRows": { },
              "& .MuiInputBase-root-MuiTablePagination-select": { },
              "& .MuiTablePagination-select":  { fontSize: "0.875rem" },
            }}
            SelectProps={{
              MenuProps: {
                PaperProps: {
                  sx: {
                    "& .MuiList-root": {
                      py: 0,
                    },
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem",
                      height: 32,
                    },
                  },
                },
              },
            }}
          />
        </Stack>
        <EventDetailDrawer open={drawerOpen} onClose={handleDrawerClose} event={selectedEvent} event_Type="waf" />
      </Stack>
    </>
  );
};
export default WebApplicationwafDetectionDetail;
